import { faArrowRight, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { SalaryIncomeDetailsForm } from ".";
import { MaximumLimits } from "../../../config/filingConstants";
import { IAddIncomeForm, IFormITR1, ISalaryFormTypes } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

type Props = { activeTab: "INCOME" | "DEDUCTION" };

const SalaryIncomeDetails: React.FC<Props> = (props: Props) => {
	const { activeTab } = props;

	const [showAddIncomeCanvas, setShowAddIncomeCanvas] = useState<ISalaryFormTypes>(null);
	const [showDeleteModal, setShowDeleteModal] = useState<IAddIncomeForm | null>(null);
	const [activeItem, setActiveItem] = useState<IAddIncomeForm | null>(null);
	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const SalaryDetails = values.IncomeDetails.Salaries || [];

	const handleDeleteSalaryItem = async () => {
		SalaryDetails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.Salaries", SalaryDetails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleSalaryIncomeCalculation();
	};

	const handleSalaryIncomeCalculation = () => {
		let TotalTDSonSalaries = 0;
		let TotalSalary = 0;
		let TotalPerquisitesValue = 0;
		let TotalProfitsInSalary = 0;
		let TotalUs89ANotified = 0;
		let TotalUs89ANonNotified = 0;
		let TotalIncomeTaxablePrevYr89A = 0;
		let TotalGrossSalary = 0;
		let TotalExemptUs10 = 0;
		let TotalNetSalary = 0;
		let TotalDeductionUs16 = 0;
		let TotalDeductionUs16ia = 50000;
		let TotalEntertainmentAlw16ii = 0;
		let TotalProfessionalTaxUs16iii = 0;
		let TotalReliefUs89 = 0;
		let TotalIncClaimedForReliefUs89A = 0;

		SalaryDetails.forEach((item) => {
			TotalTDSonSalaries += Number(item?.TotalTDSSal || 0);
			TotalSalary += Number(item?.SalaryAsPerSection17_1 || 0);
			TotalPerquisitesValue += Number(item?.PerquisitesUnderSection17_2 || 0);
			TotalProfitsInSalary += Number(item?.ProfitsInLieuOfSalaryUnderSection17_3 || 0);
			TotalUs89ANotified += Number(item?.IncomeUnderSection89ANotifiedCountry || 0);
			TotalUs89ANonNotified += Number(item?.IncomeUnderSection89AOtherThanNotifiedCountry || 0);
			TotalIncomeTaxablePrevYr89A += Number(item?.IncomeTaxableDuringThePreviousYear89A || 0);
			TotalGrossSalary += Number(item?.GrossSalary || 0);
			TotalExemptUs10 += Number(item?.ExemptAllowancesUnderSection10 || 0);
			TotalNetSalary += Number(item?.NetSalary || 0);
			TotalEntertainmentAlw16ii += Number(item?.EntertainmentAllowance16ii || 0);
			TotalProfessionalTaxUs16iii += Number(item?.ProfessionalTaxUnderSection16iii || 0);
			TotalReliefUs89 += Number(item?.ReliefUnderSection89 || 0);
			TotalIncClaimedForReliefUs89A += Number(item?.IncomeClaimedForReliefUnderSection89A || 0);
		});

		TotalProfessionalTaxUs16iii = Math.min(TotalProfessionalTaxUs16iii, MaximumLimits.ProfTax16iii);
		TotalDeductionUs16 +=
			TotalDeductionUs16ia + TotalEntertainmentAlw16ii + TotalProfessionalTaxUs16iii;

		const TotalIncomeFromSal = TotalNetSalary
			? TotalNetSalary - TotalDeductionUs16 - TotalReliefUs89 - TotalIncClaimedForReliefUs89A
			: 0;

		const DATA = {
			TotalTDSonSalaries: TotalTDSonSalaries,
			GrossSalary: TotalGrossSalary,
			Salary: TotalSalary,
			PerquisitesValue: TotalPerquisitesValue,
			ProfitsInSalary: TotalProfitsInSalary,
			NetSalary: TotalNetSalary,
			DeductionUs16: TotalDeductionUs16,
			DeductionUs16ia: TotalDeductionUs16ia,
			EntertainmentAlw16ii: TotalEntertainmentAlw16ii,
			ProfessionalTaxUs16iii: TotalProfessionalTaxUs16iii,
			TotalReliefUs89: TotalReliefUs89,
			TotalIncClaimedForReliefUs89A: TotalIncClaimedForReliefUs89A,
			IncomeFromSal: TotalIncomeFromSal
		};

		setValues({
			...cloneDeep(values),
			...DATA,
			IncomeDetails: { ...cloneDeep(values.IncomeDetails), Salaries: SalaryDetails }
		});
		setFieldValue("IncomeDetails.Salaries", SalaryDetails);
	};

	return (
		<>
			<section className={classNames(activeTab === "DEDUCTION" && "mb-5")}>
				{activeTab === "INCOME" ? (
					<PreventEditComponent>
						<div className="section-title-wrap">
							<Button variant="link" onClick={() => setShowAddIncomeCanvas(() => "MANUAL")}>
								Add Income Manually
							</Button>
							<Button variant="primaryDark" onClick={() => setShowAddIncomeCanvas(() => "UPLOAD")}>
								Upload Form 16 to fill this form instantly
								<FASvgIcon icon={faArrowRight} />
							</Button>
						</div>
					</PreventEditComponent>
				) : (
					<div className="section-title-wrap">
						<h5>
							TDS on Salary
							<small>
								This is prefilled from Salary TDS entries. amount is readonly, edit button leads to
								Salary details
							</small>
						</h5>
					</div>
				)}

				<dl className="eds-list eds-dl eds-inline-between">
					{SalaryDetails?.map((income, index) => (
						<div key={`salary-${index}`} className="eds-list--item">
							<dt>{income.EmployerName}</dt>
							<dd>
								<span>
									<PriceDisplay
										price={
											activeTab === "INCOME"
												? income?.SalaryIncomeChargeable
													? Number(income.SalaryIncomeChargeable) + 50000
													: 0
												: Number(income?.TotalTDSSal || 0)
										}
									/>
								</span>
								<Button
									variant="link"
									onClick={() => {
										setShowAddIncomeCanvas(() => "MANUAL");
										setActiveItem(() => income);
										setActiveItemIdx(() => String(index));
									}}
								>
									View
									<PreventEditComponent>
										<>/Edit</>
									</PreventEditComponent>
								</Button>
								{activeTab === "INCOME" ? (
									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => income);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								) : null}
							</dd>
						</div>
					))}
				</dl>
			</section>

			<SalaryIncomeDetailsForm
				showCanvas={!!showAddIncomeCanvas}
				formType={showAddIncomeCanvas}
				handleCloseBtnPress={(salaryData) => {
					if (salaryData) {
						if (activeItem) {
							SalaryDetails.splice(Number(activeItemIdx), 1, salaryData);
						} else {
							SalaryDetails.push(salaryData);
						}
						handleSalaryIncomeCalculation();
					}
					setShowAddIncomeCanvas(() => null);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete {showDeleteModal?.EmployerName}</p>}
				onYesBtnClick={() => handleDeleteSalaryItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default SalaryIncomeDetails;
